import { useTranslation } from 'react-i18next';

import { BootSyncLine } from '@/components/BootSplash';

import { greetingKeyForHour } from '../../homeMeta';

// =============================================================================
// HomeGreeting
// =============================================================================

export interface HomeGreetingProps {
  /** 使用者顯示名;空字串 / 未提供時走 i18n `greeting.guest`(避免寫死中文「訪客」)。 */
  userName: string;
  /** 預設 `() => new Date()` — 注入點供測試 mock 固定日期。 */
  now?: () => Date;
  /**
   * 首頁三支獨立 fetch 還有任何一支在飛 → 在日期後面補一句「正在同步資料」。
   *
   * ⚠️ 這一列刻意**不擋畫面**:骨架卡本來就畫著,這句只是把「骨架 = 還在載」講明,
   * 免得使用者把載入中的空卡讀成「我的資料是空的」。載完自然收起。
   */
  syncing?: boolean;
}

/**
 * 「早安,CY · 2026 年 5 月 27 日 · 星期三」+ 右上「建立新專案」primary CTA。
 * 時段判斷:05-10 早安 / 11-17 午安 / 18-04 晚安(跨 0 點走晚安)。
 */
export function HomeGreeting({
  userName,
  now = () => new Date(),
  syncing = false,
}: HomeGreetingProps) {
  const { t, i18n } = useTranslation('home');
  // userName 空時走 i18n guest(訪客)— 不在元件外寫死中文,en/ja 才不會永遠顯「訪客」。
  const displayName = userName.trim() || t('greeting.guest');
  const date = now();
  const hour = date.getHours();
  const greetKey = greetingKeyForHour(hour);

  const dateFmt = new Intl.DateTimeFormat(i18n.language || 'zh-TW', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long',
  });
  const dateLabel = dateFmt.format(date);

  return (
    // R292.18(owner:「午安 XXX 日期 XXX 這邊參考 D3,看起來已經變成一列然後全都呈現了」)
    // —— D3 首頁是 **「午安,Alpha」大字 + 日期小字同一行**;右邊那顆「建立新專案」
    // 也照 owner 指示退場(側欄與專案頁都有入口)。
    <div
      data-testid="home-greeting"
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 14,
        flexWrap: 'wrap',
        marginBottom: 24,
      }}
    >
      <h1
        style={{
          fontSize: 24,
          fontWeight: 800,
          color: 'var(--text-strong)',
          margin: 0,
          letterSpacing: '-0.01em',
        }}
      >
        {t(greetKey, { name: displayName })}
      </h1>
      <span style={{ fontSize: 13, color: 'var(--ink-500)' }}>{dateLabel}</span>
      {syncing && <BootSyncLine />}
    </div>
  );
}
